import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertCircle, Search, Filter, Building2, Download, ArrowUp, ArrowDown, ArrowUpDown, Settings, Save, XCircle } from 'lucide-react';
import api, { adminInvoicesAPI, invoicesAPI, adminSettingsAPI } from '../../services/api';
import { useDisplay } from '../../hooks/useDisplay';
import { isPastDate } from '../../utils/dates';
import { formatMoney } from '../../utils/format';
import { notifyError } from '../../context/toastStore';

const STORAGE_KEY = 'admin-invoices-filters';
const SETTINGS_KEY = 'invoices.defaultFilters';

const getSavedFilters = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const saveFilters = (filters) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch {}
};

export default function AdminInvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const d = useDisplay();
  const savedFilters = getSavedFilters();
  const [adminDefaults, setAdminDefaults] = useState(null);
  const [showDefaultMenu, setShowDefaultMenu] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const initialLoadDone = useRef(false);
  const lastRefreshTimestamp = useRef(null);
  const isRestoringFromUrl = useRef(false);

  // Get initial value: URL > localStorage > admin defaults > hardcoded
  // `defaults` is passed on first load: the state set a line earlier is not
  // visible to this closure yet, so the saved defaults never applied.
  const getInitialValue = (param, hardcodedDefault = '', defaults = adminDefaults) => {
    const urlValue = searchParams.get(param);
    if (urlValue !== null) return urlValue;
    if (savedFilters[param] !== undefined) return savedFilters[param];
    if (defaults?.[param] !== undefined) return defaults[param];
    return hardcodedDefault;
  };
  const firstFetchDone = useRef(false);

  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [psaStatusFilter, setPsaStatusFilter] = useState('all');
  const [agingFilter, setAgingFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
  });

  // Store all invoices when doing client-side status sorting
  const [allInvoices, setAllInvoices] = useState([]);
  const [isStatusSort, setIsStatusSort] = useState(false);

  // Calculate aging bucket for an invoice. We parse the dueDate as a local
  // calendar date (not UTC) so users west of UTC don't see invoices age a day
  // early.
  const parseLocalDate = (value) => {
    if (!value) return null;
    const datePart = String(value).split('T')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  const getAgingBucket = (invoice) => {
    const balance = invoice.balance || 0;
    if (balance <= 0) return null; // Paid invoices have no aging

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = parseLocalDate(invoice.dueDate);
    if (!dueDate) return 'current';

    const daysPastDue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));

    if (daysPastDue <= 0) return 'current';
    if (daysPastDue <= 30) return '1-30';
    if (daysPastDue <= 60) return '31-60';
    if (daysPastDue <= 90) return '61-90';
    return '90plus';
  };

  const fetchInvoices = async (page = 1, field = sortField, dir = sortDir) => {
    setLoading(true);
    setError(null);

    try {
      const isStatusField = field === 'status/name';
      const needsClientSideProcessing = isStatusField || agingFilter !== 'all';
      setIsStatusSort(needsClientSideProcessing);

      const params = {
        page: needsClientSideProcessing ? 1 : page,
        limit: needsClientSideProcessing ? 1000 : pagination.limit, // Fetch all for client-side processing
        sortField: isStatusField ? 'id' : field,
        sortDir: isStatusField ? 'desc' : dir,
      };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (psaStatusFilter && psaStatusFilter !== 'all') params.psaStatus = psaStatusFilter;
      if (companyFilter) params.companyId = companyFilter;

      const response = await adminInvoicesAPI.list(params);
      let invoiceData = response.data.invoices;

      // Apply aging filter client-side
      if (agingFilter !== 'all') {
        invoiceData = invoiceData.filter((inv) => getAgingBucket(inv) === agingFilter);
      }

      let total, pages;
      if (needsClientSideProcessing) {
        // Client-side processing: calculate from filtered data
        total = invoiceData.length;
        pages = Math.ceil(total / pagination.limit);
      } else {
        // Server-side pagination: use backend's total count
        total = response.data.pagination?.total || invoiceData.length;
        pages = response.data.pagination?.pages || Math.ceil(total / pagination.limit);
      }

      if (isStatusField) {
        // Sort all invoices by status (client-side), then by dueDate
        invoiceData = [...invoiceData].sort((a, b) => {
          const aStatus = getStatusValue(a);
          const bStatus = getStatusValue(b);
          if (aStatus !== bStatus) {
            return dir === 'asc' ? aStatus - bStatus : bStatus - aStatus;
          }
          // Secondary sort by dueDate
          const aDate = new Date(a.dueDate || 0);
          const bDate = new Date(b.dueDate || 0);
          return dir === 'asc' ? aDate - bDate : bDate - aDate;
        });
      }

      if (needsClientSideProcessing) {
        setAllInvoices(invoiceData);
        // Paginate client-side
        const startIdx = (page - 1) * pagination.limit;
        invoiceData = invoiceData.slice(startIdx, startIdx + pagination.limit);
      }

      setInvoices(invoiceData);
      setPagination((prev) => ({
        ...prev,
        page,
        total,
        pages,
      }));

      // Update URL params
      const newParams = new URLSearchParams();
      if (page > 1) newParams.set('page', page);
      if (search) newParams.set('search', search);
      if (statusFilter !== 'all') newParams.set('status', statusFilter);
      if (psaStatusFilter !== 'all') newParams.set('psaStatus', psaStatusFilter);
      if (agingFilter !== 'all') newParams.set('aging', agingFilter);
      if (companyFilter) newParams.set('company', companyFilter);
      if (field !== 'id') newParams.set('sortField', field);
      if (dir !== 'desc') newParams.set('sortDir', dir);
      setSearchParams(newParams, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies and admin defaults on mount
  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch companies
      try {
        const response = await api.get('/admin/companies?limit=500');
        setCompanies(response.data.companies || []);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
      // Fetch admin defaults
      let defaults = null;
      try {
        const res = await adminSettingsAPI.get(SETTINGS_KEY);
        if (res.data?.setting?.value) {
          defaults = res.data.setting.value;
          setAdminDefaults(defaults);
        }
      } catch {}
      // Apply initial filter values from URL > localStorage > admin defaults
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        const initialStatus = getInitialValue('status', 'all', defaults);
        const initialPsaStatus = getInitialValue('psaStatus', 'all', defaults);
        const initialAging = getInitialValue('aging', 'all', defaults);
        const initialCompany = getInitialValue('company', '', defaults);
        const initialPage = parseInt(getInitialValue('page', '1', defaults)) || 1;
        const initialSortField = searchParams.get('sortField') || 'id';
        const initialSortDir = searchParams.get('sortDir') || 'desc';
        setStatusFilter(initialStatus);
        setPsaStatusFilter(initialPsaStatus);
        setAgingFilter(initialAging);
        setCompanyFilter(initialCompany);
        setSortField(initialSortField);
        setSortDir(initialSortDir);
        setPagination(prev => ({ ...prev, page: initialPage }));
        setIsInitialized(true);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isInitialized && !isRestoringFromUrl.current) {
      // The URL's page on first load; page 1 whenever a filter changes.
      const page = firstFetchDone.current ? 1 : pagination.page;
      firstFetchDone.current = true;
      fetchInvoices(page);
    }
  }, [isInitialized, statusFilter, psaStatusFilter, agingFilter, companyFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices(1);
  };

  // Calculate status for sorting: 1=Overdue, 2=Open, 3=Partial, 4=Paid
  // Ascending: Overdue → Open → Partial → Paid
  // Descending: Paid → Partial → Open → Overdue
  const getStatusValue = (invoice) => {
    const balance = invoice.balance || 0;
    const total = invoice.total || 0;
    const isOverdue = invoice.dueDate && isPastDate(invoice.dueDate) && balance > 0;

    if (balance <= 0) return 4; // Paid
    if (isOverdue) return 1; // Overdue
    if (balance < total) return 3; // Partial
    return 2; // Open
  };

  const handleSort = (field) => {
    let newDir = 'asc';
    if (sortField === field && sortDir === 'asc') {
      newDir = 'desc';
    }
    setSortField(field);
    setSortDir(newDir);
    // For status sorting, go to page 1 since we're fetching all data
    // For other fields, stay on current page
    fetchInvoices(field === 'status/name' ? 1 : pagination.page, field, newDir);
  };

  // Handle pagination for status-sorted data (client-side)
  const handlePageChange = (page) => {
    if (isStatusSort && allInvoices.length > 0) {
      const startIdx = (page - 1) * pagination.limit;
      setInvoices(allInvoices.slice(startIdx, startIdx + pagination.limit));
      setPagination((prev) => ({ ...prev, page }));
      // Update URL
      const newParams = new URLSearchParams(searchParams);
      if (page > 1) {
        newParams.set('page', page.toString());
      } else {
        newParams.delete('page');
      }
      setSearchParams(newParams, { replace: true });
    } else {
      fetchInvoices(page);
    }
  };

  // Save current filters to localStorage and URL
  const updateFilters = (updates) => {
    const newFilters = {
      status: updates.status ?? statusFilter,
      psaStatus: updates.psaStatus ?? psaStatusFilter,
      aging: updates.aging ?? agingFilter,
    };
    saveFilters(newFilters);
  };

  const handleSaveAsDefault = async () => {
    setSavingDefault(true);
    try {
      const currentFilters = { status: statusFilter, psaStatus: psaStatusFilter, aging: agingFilter };
      await adminSettingsAPI.set(SETTINGS_KEY, currentFilters, 'Default filters for the Admin Invoices page');
      setAdminDefaults(currentFilters);
      setShowDefaultMenu(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save the default filters');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleClearLocalStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDefaultMenu(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDefaultMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-settings-dropdown')) setShowDefaultMenu(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDefaultMenu]);

  // Auto-refresh when returning from detail page - restore filters from URL and refetch
  useEffect(() => {
    if (!isInitialized) return;
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      isRestoringFromUrl.current = true;

      // Restore filter states from URL params
      const urlSearch = searchParams.get('search') || '';
      const urlStatus = searchParams.get('status') || 'all';
      const urlPsaStatus = searchParams.get('psaStatus') || 'all';
      const urlAging = searchParams.get('aging') || 'all';
      const urlCompany = searchParams.get('company') || '';
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlSortField = searchParams.get('sortField') || 'id';
      const urlSortDir = searchParams.get('sortDir') || 'desc';

      setSearch(urlSearch);
      setStatusFilter(urlStatus);
      setPsaStatusFilter(urlPsaStatus);
      setAgingFilter(urlAging);
      setCompanyFilter(urlCompany);
      setSortField(urlSortField);
      setSortDir(urlSortDir);
      setPagination(prev => ({ ...prev, page: urlPage }));

      // Defer fetch to after state updates
      setTimeout(() => {
        fetchInvoices(urlPage, urlSortField, urlSortDir);
        isRestoringFromUrl.current = false;
      }, 0);
    }
  }, [location.state?.refresh, isInitialized, searchParams]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-neutral-300" />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-primary-500" />
    ) : (
      <ArrowDown className="w-4 h-4 text-primary-500" />
    );
  };

  const handleDownloadPdf = async (e, invoice) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await invoicesAPI.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      notifyError('The PDF could not be downloaded.', err);
    }
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle timezone issues for dates that come as UTC midnight
    let normalized = dateString;
    if (typeof dateString === 'string') {
      const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        normalized = dateMatch[1] + 'T12:00:00';
      }
    }
    return new Date(normalized).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (invoice) => {
    const balance = invoice.balance || 0;
    const total = invoice.total || 0;
    const isOverdue = invoice.dueDate && isPastDate(invoice.dueDate) && balance > 0;

    if (balance <= 0) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
          Paid
        </span>
      );
    }
    if (isOverdue) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300">
          Overdue
        </span>
      );
    }
    if (balance < total) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300">
          Partial
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
        Open
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Invoices</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            View and manage customer invoices
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by invoice #, company, or billing company..."
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-neutral-400" />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[200px] bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="">All Companies</option>
            {companies.map((company) => (
              <option key={company.cw_company_id} value={company.cw_company_id}>
                {company.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); updateFilters({ status: e.target.value }); }}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="all">All Invoices</option>
            <option value="unpaid">Unpaid (Balance &gt; 0)</option>
            <option value="paid">Paid (Balance = 0)</option>
          </select>
          <select
            value={psaStatusFilter}
            onChange={(e) => { setPsaStatusFilter(e.target.value); updateFilters({ psaStatus: e.target.value }); }}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="all">All PSA Statuses</option>
            <option value="New">New</option>
            <option value="Approved">Approved</option>
            <option value="Review">Review</option>
            <option value="Changes">Changes</option>
            <option value="Sent">Sent</option>
            <option value="Email for payment">Email for payment</option>
            <option value="Send Gratis">Send Gratis</option>
            <option value="Redo this Invoice">Redo this Invoice</option>
            <option value="Closed">Closed</option>
            <option value="Closed>Emailed">Closed&gt;Emailed</option>
            <option value="Closed>Mailed">Closed&gt;Mailed</option>
            <option value="Closed>Do Not Trigger">Closed&gt;Do Not Trigger</option>
            <option value="Closed>Included In Agreement">Closed&gt;Included In Agreement</option>
          </select>
          <select
            value={agingFilter}
            onChange={(e) => { setAgingFilter(e.target.value); updateFilters({ aging: e.target.value }); }}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="all">All Aging</option>
            <option value="current">Current (Not Due)</option>
            <option value="1-30">1-30 Days Past Due</option>
            <option value="31-60">31-60 Days Past Due</option>
            <option value="61-90">61-90 Days Past Due</option>
            <option value="90plus">90+ Days Past Due</option>
          </select>

          {/* Settings dropdown */}
          <div className="relative filter-settings-dropdown">
            <button
              onClick={() => setShowDefaultMenu(!showDefaultMenu)}
              className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              title="Filter settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            {showDefaultMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-10">
                <div className="p-2 border-b border-neutral-200 dark:border-neutral-700">
                  <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Filter Defaults</p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleSaveAsDefault}
                    disabled={savingDefault}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <Save className="w-4 h-4" />
                    {savingDefault ? 'Saving...' : 'Save current as admin default'}
                  </button>
                  <button
                    onClick={handleClearLocalStorage}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <XCircle className="w-4 h-4" />
                    Clear my saved preference
                  </button>
                </div>
                {adminDefaults && (
                  <div className="p-2 border-t border-neutral-200 dark:border-neutral-700 text-xs text-neutral-500 dark:text-neutral-400">
                    <p className="font-medium mb-1">Current admin defaults:</p>
                    <p>Status: {adminDefaults.status || 'All'}</p>
                    <p>PSA: {adminDefaults.psaStatus || 'All'}</p>
                    <p>Aging: {adminDefaults.aging || 'All'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Invoice Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-neutral-400 dark:text-neutral-500" />
          <p className="text-neutral-500 dark:text-neutral-400">No invoices found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th
                    className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('invoiceNumber')}
                  >
                    <div className="flex items-center gap-1">
                      {d.label('invoicesList.col.number')}
                      <SortIcon field="invoiceNumber" />
                    </div>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('company/name')}
                  >
                    <div className="flex items-center gap-1">
                      Company
                      <SortIcon field="company/name" />
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Billing Company
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center gap-1">
                      {d.label('invoicesList.col.date')}
                      <SortIcon field="date" />
                    </div>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('dueDate')}
                  >
                    <div className="flex items-center gap-1">
                      {d.label('invoicesList.col.dueDate')}
                      <SortIcon field="dueDate" />
                    </div>
                  </th>
                  <th
                    className="text-right px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('total')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {d.label('invoicesList.col.total')}
                      <SortIcon field="total" />
                    </div>
                  </th>
                  <th
                    className="text-right px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('balance')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      {d.label('invoicesList.col.balance')}
                      <SortIcon field="balance" />
                    </div>
                  </th>
                  <th
                    className="text-center px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('status/name')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Payment
                      <SortIcon field="status/name" />
                    </div>
                  </th>
                  <th
                    className="text-center px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('status/name')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      PSA Status
                      <SortIcon field="status/name" />
                    </div>
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                    onClick={() => {
                      sessionStorage.setItem('adminInvoicesReturnUrl', window.location.pathname + window.location.search);
                      window.location.href = `/admin/invoices/${invoice.id}`;
                    }}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/invoices/${invoice.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          sessionStorage.setItem('adminInvoicesReturnUrl', window.location.pathname + window.location.search);
                        }}
                        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-neutral-400" />
                        <span className="text-neutral-900 dark:text-white truncate max-w-[200px]">
                          {invoice.company?.name || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {invoice.billToCompany?.name && invoice.billToCompany.name !== invoice.company?.name ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-neutral-400" />
                          <span className="text-neutral-900 dark:text-white truncate max-w-[200px]">
                            {invoice.billToCompany.name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-neutral-400 dark:text-neutral-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      {formatDate(invoice.date || invoice.invoiceDate)}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      {formatDate(invoice.dueDate)}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-900 dark:text-white font-medium">
                      {formatCurrency(invoice.total)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={invoice.balance > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-green-600 dark:text-green-400'}>
                        {formatCurrency(invoice.balance)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(invoice)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
                        {invoice.status?.name || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => handleDownloadPdf(e, invoice)}
                        className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} invoices
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page <= 1}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-neutral-600 dark:text-neutral-400 px-3">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(pagination.pages)}
              disabled={pagination.page >= pagination.pages}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
